import React, { FC, useState } from 'react';

import {
  Box,
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikMultiSelect from 'components/primitives/FormikMultiSelect';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';

import { selectors } from 'store/slices/washhub';

import { days, pickupFrequency } from 'constants/weekdays';

import {
  initialValues,
  parseGoogleGeocoderResultIntoAddressComponents,
  validationSchema,
} from './AddCollectionPointModal.utils';

import { AddCollectionPointViewProps } from './AddCollectionPointModal.props';
import getSortedData from 'utils/getSortedData';
import { SiteFilterableSelect } from '../SiteFilterableSelect';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const AddCollectionPointModalView: FC<AddCollectionPointViewProps> = (
  props,
) => {
  const {
    sites,
    isOpen,
    onClose,
    onCreate,
    isCreating,
    isLoadingSites,
  } = props;

  const washHub = selectors.useSelectedWashHub();

  const [showZipInput, setShowZipInput] = useState(false);

  const handleOnSelectPlacesAutocomplete = (
    result: google.maps.GeocoderResult[],
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined,
    ) => void,
  ) => {
    setShowZipInput(false);
    const addressComponents = parseGoogleGeocoderResultIntoAddressComponents(
      result,
    );
    setFieldValue('address', addressComponents);

    const shouldToCheckForShowZipInput =
      result.length > 0 && !Boolean(addressComponents.zip);
    if (shouldToCheckForShowZipInput) {
      setShowZipInput(true);
    }
  };

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader color="support.500" textAlign="center">
          Create new collection point
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onCreate}>
              {({ values, isValid, setFieldValue }) => (
                <Form>
                  <Flex>
                    <FormikInput
                      name="name"
                      label="Name (*)"
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                      placeholder="E.g. 'John's Restaurant'"
                    />
                    <Box h="20px" w="20px" />
                    <FormikSelectInput
                      placeholder="Select Type"
                      label="Type"
                      isLoading={isLoadingSites}
                      containerProps={{ flex: 1 }}
                      name="type">
                      <option value="campus">Campus</option>
                      <option value="city-wide">City-wide</option>
                      <option value="site">Site</option>
                    </FormikSelectInput>
                    <Box h="20px" w="20px" />
                    <SiteFilterableSelect
                      containerProps={{ flex: 1 }}
                      label="Linked Site"
                      name="site"
                      labelProps={labelProps}
                      isRequired={false}
                    />
                  </Flex>
                  <Flex mt={6}>
                    <FormikInput
                      name="washHub"
                      label="Wash Hub"
                      labelProps={labelProps}
                      isReadOnly
                      containerProps={{ flex: 1 }}
                      value={washHub?.name || ''}
                    />
                    <Box h="20px" w="20px" />
                    <FormikSelectInput
                      placeholder="Select Pickup Frequency"
                      label="Pickup Frequency (*)"
                      name="pickupFrequency"
                      containerProps={{ flex: 1 }}>
                      {getSortedData(pickupFrequency).map((w) => (
                        <option key={w} value={w}>
                          {w || ''}
                        </option>
                      ))}
                    </FormikSelectInput>
                    <Box h="20px" w="2px" />
                    {['Weekly', 'Bi-Weekly', 'Monthly'].includes(
                      values?.pickupFrequency,
                    ) && (
                      <FormikMultiSelect
                        options={days}
                        containerProps={{ flex: 1 }}
                        placeholder="Select"
                        label="Pickup Day Of The Week"
                        name="pickupDayOfTheWeek"
                      />
                    )}
                  </Flex>
                  <Flex mt={6}>
                    <PlacesAutocomplete
                      name="address"
                      labelText="Address (*)"
                      labelProps={labelProps}
                      onSelect={(result: google.maps.GeocoderResult[]) =>
                        handleOnSelectPlacesAutocomplete(result, setFieldValue)
                      }
                    />
                    {showZipInput && (
                      <>
                        <Box h="20px" w="20px" />
                        <FormikInput
                          name="address.zip"
                          label="Zip code (*)"
                          labelProps={labelProps}
                          containerProps={{ flex: 1 }}
                          placeholder="your text here"
                          onInput={(value) => {
                            setFieldValue('address.zip', value);
                          }}
                        />
                      </>
                    )}
                    <Box h="20px" w="20px" />
                    <FormikInput
                      name="deliveryInstructions"
                      label="Pickup Instructions"
                      placeholder="Add any pickup instructions..."
                      labelProps={labelProps}
                      containerProps={{ flex: 1 }}
                    />
                  </Flex>
                  <Flex my={12} flexDirection="row" justifyContent="center">
                    <Button
                      variant="outline"
                      marginRight="10px"
                      isDisabled={isCreating}
                      w="134px"
                      onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      isLoading={isCreating}
                      isDisabled={!isValid}
                      w="134px">
                      Create
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddCollectionPointModalView;
